import { useCallback, useState } from "react";

import { useMutation } from "@apollo/client";
import { useIntl } from "react-intl";

import { useBlizzardEvents } from "../../analytics/hooks/useBlizzardEvents";
import { type Visibility } from "../../gql-generated/types";
import { type LensPreviewMediaItem, mapPreviewMediaItemsToResources } from "../../shared/state";
import {
    createWebLensUpload as createWebLensUploadMutation,
    submitWebLens as submitWebLensMutation,
} from "../api/mutations";
import {
    type CreateWebLensUploadMutation,
    type CreateWebLensUploadMutationVariables,
} from "../api/mutations/createWebLensUpload.gql-generated";
import {
    type SubmitWebLensMutation,
    type SubmitWebLensMutationVariables,
} from "../api/mutations/submitWebLens.gql-generated";
import { WebLensUploadError, putToSignedUrl } from "../api/putToSignedUrl";
import { messages } from "../components/messages.generated";
import { type SubmissionCheck, type WebLensSubmitResult } from "../model";
import { describeWebLensCheckLabel } from "../utils/describeWebLensCheckLabel";
import { type WebLensBuildOutputMeasurement } from "../utils/measureWebLensBuildOutput";
import {
    captureWebLensException,
    failedCheckReason,
    startWebLensAttempt,
    uploadFailureReason,
} from "../utils/webLensSentry";
import { preChecksPassed, runClientPreChecks } from "../validation/preChecks";

interface SubmitWebLensInput {
    file: File;
    logo: File | null;
    name: string;
    visibility: Visibility;
    previewMediaItems: LensPreviewMediaItem[];
    /** Target Lens Space — chosen on the start page; LC authorizes the caller against it. */
    folderId: string;
    /** Enroll in Lens+ (exclusive) — create time is the only moment Lens Central accepts it. */
    isMarkedExclusive: boolean;
    /** Enroll in Top Performer Payouts (Lens Creator Rewards). */
    lensCreatorPayoutEnrolled: boolean;
    /** Creator-declared game marking — the submission flow's toggle. */
    isGameUserProvided: boolean;
    /** Page-level budget measurement; the budget row must not vanish when submit() takes the rail. */
    buildOutput: WebLensBuildOutputMeasurement | null;
}

interface SubmitWebLensState {
    checks: SubmissionCheck[];
    result: WebLensSubmitResult | null;
    submitting: boolean;
    submit: (input: SubmitWebLensInput) => Promise<void>;
    reset: () => void;
}

export const useSubmitWebLens = (): SubmitWebLensState => {
    const intl = useIntl();
    const [checks, setChecks] = useState<SubmissionCheck[]>([]);
    const [result, setResult] = useState<WebLensSubmitResult | null>(null);
    const [uploading, setUploading] = useState(false);
    const [executeCreateUpload] = useMutation<CreateWebLensUploadMutation, CreateWebLensUploadMutationVariables>(
        createWebLensUploadMutation,
    );
    const [executeSubmit, { loading: submitting }] = useMutation<SubmitWebLensMutation, SubmitWebLensMutationVariables>(
        submitWebLensMutation,
    );

    const { sendBlizzardEvent } = useBlizzardEvents();
    const submit = useCallback(
        async ({
            file,
            logo,
            name,
            visibility,
            previewMediaItems,
            folderId,
            isMarkedExclusive,
            lensCreatorPayoutEnrolled,
            isGameUserProvided,
            buildOutput,
        }: SubmitWebLensInput) => {
            setResult(null);
            // Attempt-owned instrumentation — see startWebLensAttempt for the outcome contract.
            const { trace: flowTrace, sendOutcome: sendOutcomeEvent } = startWebLensAttempt(
                "submit",
                sendBlizzardEvent,
                {
                    lens_plus: isMarkedExclusive,
                    payout_enrolled: lensCreatorPayoutEnrolled,
                    game_lens: isGameUserProvided,
                },
            );

            // Raw GraphQL/transport errors are never shown: they go to Sentry, and the creator sees
            // a localized fallback. Server-side validation rows (payload.validation) are different —
            // those details are creator-facing copy owned by the BFF and render verbatim.
            const describeUploadFailure = (error: unknown): string => {
                if (error instanceof WebLensUploadError) {
                    if (error.kind === "file_changed") {
                        return intl.formatMessage(messages.uploadFailedFileChanged);
                    }
                    if (error.kind === "stalled") {
                        return intl.formatMessage(messages.uploadFailedStalled);
                    }
                    if (error.kind === "http") {
                        return intl.formatMessage(messages.uploadFailedHttp, { status: error.status });
                    }
                    return intl.formatMessage(messages.uploadFailedNetwork);
                }
                captureWebLensException(error, "upload");
                return intl.formatMessage(messages.genericFailure);
            };

            const uploadCheck = (state: SubmissionCheck["state"], detail?: string): SubmissionCheck => ({
                id: "direct-upload",
                label: intl.formatMessage(messages.uploadCheckLabel),
                state,
                detail,
            });

            // Conversion happens asynchronously after submission, so the server step is just the
            // submit — archive validation results arrive later on the lens itself, not here.
            const serverCheck = (state: SubmissionCheck["state"], detail?: string): SubmissionCheck => ({
                id: "server",
                label: intl.formatMessage(messages.serverCheckLabel),
                state,
                detail,
            });

            const preChecks = runClientPreChecks(intl, file, logo, buildOutput);
            if (!preChecksPassed(preChecks)) {
                setChecks(preChecks);
                sendOutcomeEvent("web_lens_submit_failed_precheck", failedCheckReason(preChecks));
                return;
            }

            // Bytes go browser → GCS via signed URLs — the only upload path. The per-kind size
            // caps are bound into the URL signatures, so GCS itself refuses an oversized upload.
            setUploading(true);
            const endUploadStage = flowTrace.startStage("upload");
            let directUpload: { packageRef: string; logoUploaded: boolean };
            try {
                setChecks([...preChecks, uploadCheck("running")]);
                const ticketResponse = await executeCreateUpload();
                const ticket = ticketResponse.data?.createWebLensUpload;
                if (!ticket) {
                    captureWebLensException(
                        ticketResponse.errors?.[0] ?? new Error("createWebLensUpload returned no ticket"),
                        "upload",
                    );
                    setChecks([...preChecks, uploadCheck("fail", intl.formatMessage(messages.genericFailure))]);
                    sendOutcomeEvent("web_lens_submit_failed_upload", "no_ticket");
                    return;
                }
                // The caps come from the ticket — the exact values bound into the URL signatures —
                // so the header can never drift from what the BFF signed.
                await putToSignedUrl(ticket.packageUploadUrl, file, ticket.packageMaxBytes, (fraction) => {
                    setChecks([...preChecks, uploadCheck("running", `${Math.round(fraction * 100)}%`)]);
                });
                if (logo) {
                    await putToSignedUrl(ticket.logoUploadUrl, logo, ticket.logoMaxBytes);
                }
                directUpload = { packageRef: ticket.uploadRef, logoUploaded: Boolean(logo) };
            } catch (error) {
                setChecks([...preChecks, uploadCheck("fail", describeUploadFailure(error))]);
                sendOutcomeEvent("web_lens_submit_failed_upload", uploadFailureReason(error));
                return;
            } finally {
                endUploadStage();
                setUploading(false);
            }

            const baseChecks: SubmissionCheck[] = [...preChecks, uploadCheck("pass")];
            setChecks([...baseChecks, serverCheck("running")]);

            // Preview media was already uploaded as refs by the preview picker; it attaches at
            // CreateLens time (the lens is locked against updates while its first version
            // processes, so post-create attachment is not possible).
            const previewMediaUploadRefIds = mapPreviewMediaItemsToResources(previewMediaItems).flatMap((resource) =>
                resource.uploadRefId ? [resource.uploadRefId] : [],
            );

            flowTrace.startStage("mutation");
            try {
                const response = await executeSubmit({
                    variables: {
                        input: {
                            packageRef: directUpload.packageRef,
                            logoUploaded: directUpload.logoUploaded,
                            name,
                            visibility,
                            previewMediaUploadRefIds,
                            folderId,
                            // Included only when set: a BFF predating the field rejects unknown
                            // input keys, so unenrolled submissions keep working during rollout.
                            ...(isMarkedExclusive ? { isMarkedExclusive: true } : {}),
                            // Keep unenrolled submissions compatible during the BFF rollout too.
                            ...(lensCreatorPayoutEnrolled ? { lensCreatorPayoutEnrolled: true } : {}),
                            // Same rollout shape as isMarkedExclusive.
                            ...(isGameUserProvided ? { isGameUserProvided: true } : {}),
                        },
                    },
                });

                const payload = response.data?.submitWebLens;
                if (!payload) {
                    captureWebLensException(
                        response.errors?.[0] ?? new Error("submitWebLens returned an empty response"),
                        "submit",
                    );
                    setChecks([...baseChecks, serverCheck("fail", intl.formatMessage(messages.genericFailure))]);
                    sendOutcomeEvent("web_lens_submit_failed_server", "empty_payload");
                    return;
                }

                const serverChecks: SubmissionCheck[] = payload.validation.map((check) => ({
                    id: check.name,
                    label: describeWebLensCheckLabel(intl, check.name),
                    state: check.passed ? "pass" : "fail",
                    detail: check.detail ?? undefined,
                }));
                setChecks([...baseChecks, ...serverChecks]);

                if (payload.lensId) {
                    sendOutcomeEvent("web_lens_submit_success");
                    setResult({ lensId: payload.lensId, status: payload.status ?? undefined });
                } else {
                    sendOutcomeEvent("web_lens_submit_failed_server", failedCheckReason(serverChecks));
                }
            } catch (error) {
                captureWebLensException(error, "submit");
                setChecks([...baseChecks, serverCheck("fail", intl.formatMessage(messages.genericFailure))]);
                sendOutcomeEvent("web_lens_submit_failed_server", "transport");
            }
        },
        [executeCreateUpload, executeSubmit, intl, sendBlizzardEvent],
    );

    const reset = useCallback(() => {
        setChecks([]);
        setResult(null);
    }, []);

    return { checks, result, submitting: submitting || uploading, submit, reset };
};
